import { useState, useCallback } from 'react';
import { useToast } from '../components/shared/ToastProvider';

export const useInteractiveElement = (options = {}) => {
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const {
        loadingMessage = 'Processing...',
        successMessage = 'Action completed successfully',
        errorMessage = 'Action failed. Please try again.',
        showLoadingToast = true,
        showSuccessToast = true,
        showErrorToast = true,
        delay = 0
    } = options;

    const handleClick = useCallback(async (action, customMessages = {}) => {
        if (isLoading) return;

        setIsLoading(true);
        setError(null);

        const messages = {
            loading: customMessages.loading || loadingMessage,
            success: customMessages.success || successMessage,
            error: customMessages.error || errorMessage
        };

        let loadingToastId = null;

        try {
            if (showLoadingToast && messages.loading) {
                loadingToastId = showToast(messages.loading, 'info', 0);
            }

            // Add delay if specified (for better UX feedback)
            if (delay > 0) {
                await new Promise(resolve => setTimeout(resolve, delay));
            }

            const result = await (typeof action === 'function' ? action() : Promise.resolve(action));

            if (loadingToastId) {
                // Remove loading toast by hiding it
                setTimeout(() => showToast('', 'success', 1), 100); // Quick override
            }

            if (showSuccessToast && messages.success) {
                showToast(messages.success, 'success');
            }

            return result;
        } catch (err) {
            setError(err);
            
            if (loadingToastId) {
                setTimeout(() => showToast('', 'error', 1), 100); // Quick override
            }

            if (showErrorToast) {
                const errorMsg = err?.message || messages.error;
                showToast(errorMsg, 'error');
            }

            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, showToast, loadingMessage, successMessage, errorMessage, showLoadingToast, showSuccessToast, showErrorToast, delay]);

    const handleNavigation = useCallback((path, router, message = `Navigating to ${path}`) => {
        showToast(message, 'info');
        if (router && router.push) {
            router.push(path);
        } else {
            window.location.href = path;
        }
    }, [showToast]);

    const handleFormSubmit = useCallback(async (formData, submitAction, options = {}) => {
        return handleClick(async () => {
            const result = await submitAction(formData);
            return result;
        }, {
            loading: options.loading || 'Submitting form...',
            success: options.success || 'Form submitted successfully',
            error: options.error || 'Form submission failed'
        });
    }, [handleClick]);

    const handleApiCall = useCallback(async (apiCall, options = {}) => {
        return handleClick(async () => {
            const response = await apiCall();
            return response;
        }, {
            loading: options.loading || 'Loading...',
            success: options.success || 'Data loaded successfully',
            error: options.error || 'Failed to load data'
        });
    }, [handleClick]);

    return {
        isLoading,
        error,
        handleClick,
        handleNavigation,
        handleFormSubmit,
        handleApiCall,
        showToast
    };
};

// Pre-configured hooks for common interactions
export const useButtonClick = (options = {}) => {
    return useInteractiveElement({
        delay: 200, // Small delay for better feedback
        ...options
    });
};

export const useFormSubmit = (options = {}) => {
    return useInteractiveElement({
        loadingMessage: 'Submitting form...',
        successMessage: 'Form submitted successfully',
        errorMessage: 'Form submission failed',
        delay: 300,
        ...options
    });
};

export const useApiCall = (options = {}) => {
    return useInteractiveElement({
        loadingMessage: 'Loading...',
        successMessage: 'Data loaded successfully',
        errorMessage: 'Failed to load data',
        showSuccessToast: false, // Usually don't need success toast for data loading
        ...options
    });
};

export const useNavigation = () => {
    const { showToast } = useToast();

    return useCallback((path, message) => {
        const displayMessage = message || `Navigating to ${path.replace('/', '').replace('-', ' ')}`;
        showToast(displayMessage, 'info');
        
        // Use setTimeout to allow toast to show before navigation
        setTimeout(() => {
            window.location.href = path;
        }, 100);
    }, [showToast]);
};

export default useInteractiveElement;